import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { CreateInventoryRequest, Inventory } from '@/api/types'
import { Field, TextInput, SelectInput } from '@/components/common/ui'
import { StandardsPicker } from '@/components/common/StandardsPicker'
import { standardsApi } from '@/api/clients/standardsApi'
import { orgComplianceApi } from '@/api/clients/orgComplianceApi'
import { useOrgStore } from '@/stores/orgStore'

const CRITICALITY = ['low', 'medium', 'high', 'critical']
const CLASSIFICATION = ['public', 'internal', 'confidential', 'restricted', 'regulated']
const REG_SCOPE = ['unscoped', 'single_framework', 'multi_framework', 'systemic']

// RACI owners, serialized to the inventory `owners` JSON field.
interface Raci {
  responsible: string
  accountable: string
  consulted: string
  informed: string
}

const RACI_KEYS: { key: keyof Raci; label: string }[] = [
  { key: 'responsible', label: 'Responsible (R)' },
  { key: 'accountable', label: 'Accountable (A)' },
  { key: 'consulted', label: 'Consulted (C)' },
  { key: 'informed', label: 'Informed (I)' },
]

function csv(v: string): string[] {
  return v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function ownersToRaci(owners: unknown): Raci {
  const o = (owners ?? {}) as Record<string, unknown>
  const join = (v: unknown) => (Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : '')
  return {
    responsible: join(o.responsible),
    accountable: join(o.accountable),
    consulted: join(o.consulted),
    informed: join(o.informed),
  }
}

export type InventoryFormValue = CreateInventoryRequest

// Shared create/edit form. Returns the built request via onChange; parent owns submit.
export function InventoryForm({
  initial,
  onChange,
}: {
  initial?: Inventory
  onChange: (value: InventoryFormValue) => void
}) {
  const [form, setForm] = useState<CreateInventoryRequest>({
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    business_unit: initial?.business_unit ?? '',
    criticality: initial?.criticality ?? 'medium',
    data_classification: initial?.data_classification ?? 'internal',
    environment: initial?.environment ?? 'production',
    regulatory_scope: initial?.regulatory_scope ?? 'unscoped',
    regulatory_frameworks: initial?.regulatory_frameworks ?? [],
  })
  const [raci, setRaci] = useState<Raci>(ownersToRaci(initial?.owners))

  const { currentOrgId: orgId } = useOrgStore()
  // Org default standards + the standards catalogue — used to show what an inventory with
  // no frameworks of its own inherits.
  const orgCompliance = useQuery({
    queryKey: ['org-compliance', orgId],
    queryFn: () => orgComplianceApi.get(orgId),
    enabled: !!orgId,
  })
  const standards = useQuery({
    queryKey: ['standards', orgId],
    queryFn: () => standardsApi.list(orgId),
    enabled: !!orgId,
  })
  const inheritedNames = (orgCompliance.data?.standards ?? [])
    .map((k) => {
      const s = standards.data?.find(
        (o) => o.name.toLowerCase() === k || (o.aliases ?? []).some((a) => a.toLowerCase() === k)
      )
      return s?.name ?? k
    })
    .join(', ')

  const emit = (nextForm: CreateInventoryRequest, nextRaci: Raci) => {
    onChange({
      ...nextForm,
      owners: {
        responsible: csv(nextRaci.responsible),
        accountable: csv(nextRaci.accountable),
        consulted: csv(nextRaci.consulted),
        informed: csv(nextRaci.informed),
      },
    })
  }

  const set = <K extends keyof CreateInventoryRequest>(k: K, v: CreateInventoryRequest[K]) => {
    const next = { ...form, [k]: v }
    setForm(next)
    emit(next, raci)
  }
  const setR = (k: keyof Raci, v: string) => {
    const next = { ...raci, [k]: v }
    setRaci(next)
    emit(form, next)
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <Field label="Name">
          <TextInput
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Payments Platform"
          />
        </Field>
      </div>
      <Field label="Business unit">
        <TextInput value={form.business_unit} onChange={(e) => set('business_unit', e.target.value)} />
      </Field>
      <Field label="Environment">
        <TextInput value={form.environment} onChange={(e) => set('environment', e.target.value)} />
      </Field>
      <Field label="Criticality">
        <SelectInput value={form.criticality} onChange={(e) => set('criticality', e.target.value)}>
          {CRITICALITY.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Data classification">
        <SelectInput
          value={form.data_classification}
          onChange={(e) => set('data_classification', e.target.value)}
        >
          {CLASSIFICATION.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Regulatory scope">
        <SelectInput
          value={form.regulatory_scope}
          onChange={(e) => set('regulatory_scope', e.target.value)}
        >
          {REG_SCOPE.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </SelectInput>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Frameworks">
          <StandardsPicker
            orgId={orgId}
            value={form.regulatory_frameworks ?? []}
            onChange={(next) => set('regulatory_frameworks', next)}
          />
          {(form.regulatory_frameworks ?? []).length === 0 && inheritedNames && (
            <p className="mt-1.5 text-xs text-subtle-light dark:text-subtle-dark">
              Inherits org defaults: {inheritedNames}
            </p>
          )}
        </Field>
      </div>

      {/* RACI owners */}
      <div className="sm:col-span-2 pt-2">
        <p className="label-sm text-subtle-light dark:text-subtle-dark mb-2">RACI owners</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {RACI_KEYS.map((r) => (
            <Field key={r.key} label={r.label}>
              <TextInput
                value={raci[r.key]}
                onChange={(e) => setR(r.key, e.target.value)}
                placeholder="alice@co, bob@co"
              />
            </Field>
          ))}
        </div>
      </div>
    </div>
  )
}
